import classNames from 'classnames/bind';
import React from 'react';
import { ColorHelper } from 'utils/colorHelper';

import IconClose from 'reactComponents/icons/close';

import styles from './Label.css';

const css = classNames.bind(styles);

export type LabelSize = 'small' | 'normal' | 'medium';

export type LabelProps = {
    children?: React.ReactNode;
    color?: string;
    bordered?: boolean;
    hoverable?: boolean;
    onClick?: React.EventHandler<React.SyntheticEvent<HTMLSpanElement>>;
    className?: string;
    onClose?(event: React.MouseEvent<SVGSVGElement, MouseEvent>): void;
    size?: LabelSize;
};

export type LabelColorExtended = {
    background?: string;
    base: string;
};

export class Label extends React.Component<LabelProps> {
    // eslint-disable-next-line react/sort-comp
    static defaultGrey = '#707070';
    public static defaultProps = {
        color: Label.defaultGrey,
    };

    public static EXTENDED_COLORS: LabelColorExtended[] = [
        { base: '#ED3E37', background: '#FFE2E1' },
        { base: '#3FB58F', background: '#DAF6ED' },
        { base: '#3F91C8', background: '#D0ECFF' },
        { base: '#DE8410', background: '#FFDFB6' },
        { base: '#7972D1', background: '#E3E1FF' },
        { base: '#CB5DB1', background: '#FFE1F8' },
        { base: '#707070', background: 'rgba(0, 0, 0, .08)' },
        { base: '#A282C4', background: '#E9D4FF' },
        { base: '#82B369', background: '#EBFFE1' },
        { base: '#BF7252', background: '#FFD4C2' },
        { base: '#6199A4', background: '#E1FAFF' },
    ];

    public static BASIC_COLORS = Object.values(Label.EXTENDED_COLORS).map(
        ({ base }) => base,
    );

    public static getExtendedColor(color: string) {
        return Label.EXTENDED_COLORS.find(({ base }) => base === color);
    }

    getStyle() {
        const { color } = this.props;
        if (!color) {
            return {};
        }
        const colorExtended = Label.getExtendedColor(color);
        if (colorExtended) {
            const { base, background } = colorExtended;
            return {
                color: base === Label.defaultGrey ? '#333' : base,
                borderColor: base,
                backgroundColor: background || this.baseColorToBackground(base),
            };
        }
        return {
            color: color === Label.defaultGrey ? '#333' : color,
            borderColor: color,
            backgroundColor: this.baseColorToBackground(color),
        };
    }

    baseColorToBackground(color: string) {
        return ColorHelper.hexToRgbaString(color, 0.2);
    }

    render() {
        const { props } = this;

        const title =
            typeof props.children === 'string' ? props.children : undefined;

        return (
            <span
                data-test="label"
                className={css('root', props.className, {
                    bordered: props.bordered,
                    hoverable: props.hoverable || props.onClick,
                    sizeSmall: props.size === 'small',
                    sizeMedium: props.size === 'medium',
                })}
                title={title}
                style={this.getStyle()}
                onClick={props.onClick}
            >
                <span className={css('inner')}>{props.children}</span>
                {props.onClose && (
                    <IconClose
                        className={css('iconClose')}
                        onClick={props.onClose}
                    />
                )}
            </span>
        );
    }
}
